import { sql } from "drizzle-orm";
import { boolean, doublePrecision, index, integer, jsonb, pgTable, timestamp, uniqueIndex, varchar } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Session storage table.
// (IMPORTANT) This table is mandatory for Replit Auth, don't drop it.
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)]
);

export const groups = pgTable("groups", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 255 }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertGroupSchema = createInsertSchema(groups).omit({
  id: true,
  createdAt: true,
});
export type InsertGroup = z.infer<typeof insertGroupSchema>;
export type Group = typeof groups.$inferSelect;

// User storage table.
// (IMPORTANT) This table is mandatory for Replit Auth, don't drop it.
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: varchar("email").unique(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  passwordHash: varchar("password_hash"),
  authVersion: integer("auth_version").default(0).notNull(),
  isMaster: boolean("is_master").default(false).notNull(),
  isSuperuser: boolean("is_superuser").default(false).notNull(),
  // Per-user capability: lets a regular (non-master) user create their own
  // brands. Existing access scoping keeps them restricted to brands they
  // created / were assigned / had shared. Granted from the admin user UI.
  canCreateBrands: boolean("can_create_brands").default(false).notNull(),
  receiveCommentNotifications: boolean("receive_comment_notifications").default(false).notNull(),
  isApproved: boolean("is_approved").default(false).notNull(),
  groupId: varchar("group_id"),
  // Soft-delete marker: null = active. Deactivated users cannot log in,
  // are hidden from listings/pickers, but keep all attributed content and
  // usage/billing history. Admins can restore (clear) this.
  deletedAt: timestamp("deleted_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type UpsertUser = typeof users.$inferInsert;
export type User = typeof users.$inferSelect;

export const passwordResetChallenges = pgTable(
  "password_reset_challenges",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }),
    emailDigest: varchar("email_digest", { length: 64 }).notNull(),
    clientDigest: varchar("client_digest", { length: 64 }).notNull(),
    codeDigest: varchar("code_digest", { length: 64 }).notNull(),
    expiresAt: timestamp("expires_at").notNull(),
    attemptCount: integer("attempt_count").notNull().default(0),
    maxAttempts: integer("max_attempts").notNull().default(5),
    consumedAt: timestamp("consumed_at"),
    deliveredAt: timestamp("delivered_at"),
    deliveryFailedAt: timestamp("delivery_failed_at"),
    requestedAt: timestamp("requested_at").notNull().defaultNow(),
  },
  (table) => [
    index("password_reset_email_requested_idx").on(table.emailDigest, table.requestedAt),
    index("password_reset_client_requested_idx").on(table.clientDigest, table.requestedAt),
    index("password_reset_user_active_idx").on(table.userId, table.consumedAt, table.expiresAt),
    index("password_reset_expiry_idx").on(table.expiresAt),
  ],
);

export type PasswordResetChallenge = typeof passwordResetChallenges.$inferSelect;

export const brandUsers = pgTable(
  "brand_users",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    brandId: varchar("brand_id").notNull(),
    userId: varchar("user_id").notNull(),
    assignedAt: timestamp("assigned_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("brand_users_unique").on(table.brandId, table.userId),
  ]
);

export const insertBrandUserSchema = createInsertSchema(brandUsers).omit({
  id: true,
  assignedAt: true,
});
export type InsertBrandUser = z.infer<typeof insertBrandUserSchema>;
export type BrandUser = typeof brandUsers.$inferSelect;

export const resourceShares = pgTable(
  "resource_shares",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    resourceType: varchar("resource_type", { length: 50 }).notNull(),
    resourceId: varchar("resource_id").notNull(),
    sharedByUserId: varchar("shared_by_user_id").notNull(),
    sharedWithUserId: varchar("shared_with_user_id").notNull(),
    permission: varchar("permission", { length: 20 }).notNull().default("view"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("resource_shares_unique").on(
      table.resourceType,
      table.resourceId,
      table.sharedWithUserId,
    ),
    index("resource_shares_user_idx").on(table.sharedWithUserId),
    index("resource_shares_resource_idx").on(table.resourceType, table.resourceId),
  ]
);

export const insertResourceShareSchema = createInsertSchema(resourceShares).omit({
  id: true,
  createdAt: true,
});
export type InsertResourceShare = z.infer<typeof insertResourceShareSchema>;
export type ResourceShare = typeof resourceShares.$inferSelect;

export const userCredits = pgTable(
  "user_credits",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull(),
    credits: doublePrecision("credits").notNull().default(0),
    balance: doublePrecision("balance").notNull().default(0),
    multiplier: doublePrecision("multiplier").notNull().default(1.0),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("user_credits_user_unique").on(table.userId),
  ]
);

export const insertUserCreditsSchema = createInsertSchema(userCredits).omit({
  id: true,
  updatedAt: true,
});
export type InsertUserCredits = z.infer<typeof insertUserCreditsSchema>;
export type UserCredits = typeof userCredits.$inferSelect;

export const modelPricing = pgTable("model_pricing", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  modelName: varchar("model_name").notNull(),
  provider: varchar("provider").notNull(),
  sourceUrl: varchar("source_url"),
  inputPer1m: doublePrecision("input_per_1m"),
  outputPer1m: doublePrecision("output_per_1m"),
  lastUpdated: timestamp("last_updated").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const unitPricing = pgTable("unit_pricing", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  modelName: varchar("model_name").notNull(),
  provider: varchar("provider").notNull(),
  sourceUrl: varchar("source_url"),
  unitType: varchar("unit_type").notNull().default("image"),
  pricingTiers: jsonb("pricing_tiers").$type<Record<string, number>>(),
  lastUpdated: timestamp("last_updated").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertUnitPricingSchema = createInsertSchema(unitPricing).omit({
  id: true,
  lastUpdated: true,
  createdAt: true,
});
export type InsertUnitPricing = z.infer<typeof insertUnitPricingSchema>;
export type UnitPricing = typeof unitPricing.$inferSelect;

export const insertModelPricingSchema = createInsertSchema(modelPricing).omit({
  id: true,
  lastUpdated: true,
  createdAt: true,
});
export type InsertModelPricing = z.infer<typeof insertModelPricingSchema>;
export type ModelPricing = typeof modelPricing.$inferSelect;

export const usageLogMetadataSchema = z.object({
  falEndpoint: z.string().optional(),
  nanoBananaSavedVersion: z.enum(["2.0", "2.1"]).optional(),
  smbOrderId: z.string().uuid().optional(),
  brandId: z.string().uuid().optional(),
  brandName: z.string().optional(),
  entityName: z.string().optional(),
  entityUrl: z.string().optional(),
  adGenerationId: z.string().optional(),
  ugcCampaignId: z.string().optional(),
  ugcScriptId: z.string().optional(),
  claymationCampaignId: z.string().optional(),
  claymationScriptId: z.string().optional(),
  videoExpressCampaignId: z.string().optional(),
  videoExpressScriptId: z.string().optional(),
  pixarCampaignId: z.string().optional(),
  pixarScriptId: z.string().optional(),
  simpsonsCampaignId: z.string().optional(),
  simpsonsScriptId: z.string().optional(),
  dayinlifeCampaignId: z.string().optional(),
  dayinlifeScriptId: z.string().optional(),
  renderId: z.string().optional(),
  unitType: z.string().optional(),
  tier: z.string().optional(),
  quantity: z.number().optional(),
  pricingStatus: z.literal("unconfigured").optional(),
  chars: z.number().optional(),
  voiceOwnerType: z.string().optional(),
  voiceOwnerId: z.string().optional(),
  voiceId: z.string().optional(),
  pageId: z.string().optional(),
  runId: z.string().optional(),
  variantHint: z.string().optional(),
  op: z.string().optional(),
  starterBatchId: z.string().optional(),
  starterJobId: z.string().optional(),
});
export const usageLogs = pgTable(
  "usage_logs",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id").notNull(),
    workflowName: varchar("workflow_name").notNull(),
    model: varchar("model"),
    inputTokens: integer("input_tokens").default(0),
    outputTokens: integer("output_tokens").default(0),
    totalTokens: integer("total_tokens").default(0),
    rawCostUsd: doublePrecision("raw_cost_usd").default(0),
    apiCostUsd: doublePrecision("api_cost_usd").default(0),
    totalCostUsd: doublePrecision("total_cost_usd").default(0),
    multiplier: doublePrecision("multiplier").default(1),
    adjustedCostUsd: doublePrecision("adjusted_cost_usd").default(0),
    creditsCharged: doublePrecision("credits_charged").default(0),
    creditsBefore: doublePrecision("credits_before").default(0),
    creditsAfter: doublePrecision("credits_after").default(0),
    executionId: varchar("execution_id"),
    // Optional exactly-once key for paid unit stages whose worker may retry
    // after a crash between ledger insertion and its own settlement marker.
    idempotencyKey: varchar("idempotency_key", { length: 200 }),
    metadata: jsonb("metadata").$type<UsageLogMetadata>(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("usage_logs_user_idx").on(table.userId),
    index("usage_logs_created_idx").on(table.createdAt),
    index("usage_logs_smb_order_idx").on(sql`(${table.metadata}->>'smbOrderId')`),
    uniqueIndex("usage_logs_idempotency_key_unique").on(table.idempotencyKey),
  ]
);

export const insertUsageLogSchema = createInsertSchema(usageLogs).omit({
  id: true,
  createdAt: true,
}).extend({
  metadata: usageLogMetadataSchema.nullable().optional(),
});
export type InsertUsageLog = z.infer<typeof insertUsageLogSchema>;
export type UsageLog = typeof usageLogs.$inferSelect;

export type UsageLogMetadata = z.infer<typeof usageLogMetadataSchema>;
